import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { FleetUser } from './LoginPage';
import { OperatorInfo, formatiereZeit } from '../types';

const MIN_PASSWORT_LAENGE = 10;

interface OperatorenPageProps {
  aktuellerBenutzer: FleetUser;
}

const OperatorenPage: React.FC<OperatorenPageProps> = ({ aktuellerBenutzer }) => {
  const [operatoren, setOperatoren] = useState<OperatorInfo[]>([]);
  const [fehler, setFehler] = useState('');
  const [meldung, setMeldung] = useState('');
  const [formularOffen, setFormularOffen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [speichert, setSpeichert] = useState(false);

  const laden = useCallback(async () => {
    setFehler('');
    try {
      const res = await api.get<OperatorInfo[]>('/api/operatoren');
      setOperatoren(res.data);
    } catch (err: any) {
      setFehler(err.response?.data?.detail || 'Operatoren konnten nicht geladen werden.');
    }
  }, []);

  useEffect(() => {
    laden();
  }, [laden]);

  const anlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    setSpeichert(true);
    setFehler('');
    setMeldung('');
    try {
      await api.post('/api/operatoren', { name, email, passwort });
      setMeldung(`Operator ${email} angelegt.`);
      setName('');
      setEmail('');
      setPasswort('');
      setFormularOffen(false);
      await laden();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setFehler(
        typeof detail === 'string'
          ? detail
          : 'Operator konnte nicht angelegt werden. E-Mail gültig? Passwort lang genug?'
      );
    } finally {
      setSpeichert(false);
    }
  };

  const umschalten = async (operator: OperatorInfo) => {
    const frage = operator.aktiv
      ? `Konto ${operator.email} deaktivieren? Ein Login ist danach nicht mehr möglich.`
      : `Konto ${operator.email} wieder aktivieren?`;
    if (!window.confirm(frage)) return;

    setFehler('');
    setMeldung('');
    try {
      await api.patch(`/api/operatoren/${operator.id}`, { aktiv: !operator.aktiv });
      await laden();
    } catch (err: any) {
      setFehler(err.response?.data?.detail || 'Änderung fehlgeschlagen.');
    }
  };

  const passwortSetzen = async (operator: OperatorInfo) => {
    const neues = window.prompt(
      `Neues Passwort für ${operator.email} (mindestens ${MIN_PASSWORT_LAENGE} Zeichen):`
    );
    if (!neues) return;
    if (neues.length < MIN_PASSWORT_LAENGE) {
      setFehler(`Das Passwort muss mindestens ${MIN_PASSWORT_LAENGE} Zeichen haben.`);
      return;
    }

    setFehler('');
    try {
      await api.patch(`/api/operatoren/${operator.id}`, { passwort: neues });
      setMeldung(`Passwort für ${operator.email} gesetzt.`);
      await laden();
    } catch (err: any) {
      setFehler(err.response?.data?.detail || 'Passwort konnte nicht gesetzt werden.');
    }
  };

  return (
    <div className="k-seite">
      <div className="k-seite__kopf">
        <div>
          <span className="k-eyebrow">{'// fleet / operatoren'}</span>
          <h1 className="k-h1">Operatoren</h1>
        </div>
        <button className="k-btn" onClick={() => setFormularOffen(!formularOffen)}>
          {formularOffen ? 'Abbrechen' : 'Operator anlegen'}
        </button>
      </div>

      {fehler && <div className="k-fehler">{fehler}</div>}
      {meldung && <div className="k-meldung">{meldung}</div>}

      {formularOffen && (
        <form className="k-formular" onSubmit={anlegen}>
          <div className="k-feld">
            <label>Name</label>
            <input value={name} onChange={e => setName(e.target.value)} required autoFocus />
          </div>
          <div className="k-feld">
            <label>E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="k-feld">
            <label>Startpasswort</label>
            <input
              type="password"
              value={passwort}
              onChange={e => setPasswort(e.target.value)}
              minLength={MIN_PASSWORT_LAENGE}
              required
            />
            <span className="k-feld__hint">
              Mindestens {MIN_PASSWORT_LAENGE} Zeichen. Der Einladungs-Flow per
              E-Mail folgt später — bis dahin teilst du das Passwort selbst mit.
            </span>
          </div>
          <button className="k-btn" type="submit" disabled={speichert}>
            {speichert ? 'Wird angelegt …' : 'Anlegen'}
          </button>
        </form>
      )}

      <div className="k-tabelle__rahmen">
        <table className="k-tabelle">
          <thead>
            <tr>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Status</th>
              <th>Angelegt</th>
              <th className="k-tabelle__aktionen">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {operatoren.map(o => {
              const ichSelbst = o.email === aktuellerBenutzer.email;
              return (
                <tr key={o.id}>
                  <td>
                    {o.name}
                    {ichSelbst && <span className="k-badge">Du</span>}
                  </td>
                  <td className="k-mono">{o.email}</td>
                  <td>{o.rolle}</td>
                  <td>
                    {o.aktiv ? 'aktiv' : 'deaktiviert'}
                    {o.gesperrt && <span className="k-badge k-badge--warn">gesperrt</span>}
                  </td>
                  <td className="k-mono">{formatiereZeit(o.erstellt_am)}</td>
                  <td className="k-tabelle__aktionen">
                    <button className="k-btn k-btn--geist" onClick={() => passwortSetzen(o)}>
                      Passwort setzen
                    </button>
                    <button
                      className="k-btn k-btn--geist"
                      disabled={ichSelbst}
                      title={ichSelbst ? 'Das eigene Konto lässt sich nicht deaktivieren' : ''}
                      onClick={() => umschalten(o)}
                    >
                      {o.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OperatorenPage;
